// Prod_US sign-in: Firebase phone authentication (text-a-code). The only Firebase file.
//
// Firebase is loaded on first use, so staff sessions never download it and the person page
// can import this file for its Prod_US branch at no cost to anyone else.
//
// Engineer: `npm install firebase` (v10+) and add these to the build's .env file:
//   REACT_APP_FIREBASE_API_KEY, REACT_APP_FIREBASE_AUTH_DOMAIN, REACT_APP_FIREBASE_PROJECT_ID,
//   REACT_APP_FIREBASE_APP_ID, REACT_APP_FIREBASE_MESSAGING_SENDER_ID
// Firebase console: Authentication > Sign-in method > Phone = on; Authorized domains = inochi.seniorlife.ai
// (and the staging / dev hosts). SMS region policy: allow US and Canada only.
const config = () => ({
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
});

let firebase = null; // { auth, ...firebase/auth functions }, once loaded
let loading = null;
let verifier = null;
let confirmation = null;

const loadFirebase = () => {
  if (loading) return loading;
  loading = Promise.all([import("firebase/app"), import("firebase/auth")])
    .then(([appModule, authModule]) => {
      const app = appModule.getApps()[0] || appModule.initializeApp(config());
      const auth = authModule.getAuth(app);
      auth.languageCode = "en";
      firebase = { ...authModule, auth };
      return firebase;
    })
    .catch((error) => {
      // A dropped connection must not leave sign-in broken until a reload.
      loading = null;
      throw error;
    });
  return loading;
};

// Invisible reCAPTCHA, in an empty container that stays on the page (ProdUsGate).
const getVerifier = ({ RecaptchaVerifier, auth }, containerId) => {
  if (verifier) return verifier;
  verifier = new RecaptchaVerifier(auth, containerId, { size: "invisible" });
  return verifier;
};

const resetVerifier = () => {
  try {
    verifier?.clear();
  } catch {
    /* already cleared */
  }
  verifier = null;
};

// Firebase error code -> Prod_US message key (prodUsStrings.js).
export const authErrorKey = (error) => {
  const code = error?.code || "";
  if (code === "auth/invalid-verification-code" || code === "auth/code-expired")
    return "code_wrong";
  if (code === "auth/too-many-requests" || code === "auth/quota-exceeded")
    return "code_locked";
  if (
    code === "auth/invalid-phone-number" ||
    code === "auth/missing-phone-number"
  )
    return "phone_req";
  return "send_failed";
};

// phoneE164 = "+15555550100". Texts the code. Call again to resend.
export const sendCode = async (phoneE164, containerId) => {
  const loaded = await loadFirebase();
  try {
    confirmation = await loaded.signInWithPhoneNumber(
      loaded.auth,
      phoneE164,
      getVerifier(loaded, containerId),
    );
    return confirmation;
  } catch (error) {
    resetVerifier();
    throw error;
  }
};

// Returns the Firebase user. Its ID token is what the backend checks on every Prod_US call.
export const verifyCode = async (code) => {
  if (!confirmation) {
    const error = new Error("No code was sent");
    error.code = "auth/code-expired";
    throw error;
  }
  const result = await confirmation.confirm(code);
  confirmation = null;
  return result.user;
};

// After a reload Firebase restores the signed-in user asynchronously; until then
// currentUser is null, which would read as signed out.
export const whenAuthRestored = async () => {
  const { auth } = await loadFirebase();
  await auth.authStateReady();
};

export const currentUser = () => firebase?.auth.currentUser ?? null;

export const getIdToken = async (forceRefresh = false) => {
  await whenAuthRestored();
  const user = currentUser();
  return user ? user.getIdToken(forceRefresh) : null;
};

// Fires on sign-in, sign-out and every hourly token refresh.
export const onTokenChange = (callback) => {
  loadFirebase()
    .then(({ auth, onIdTokenChanged }) =>
      onIdTokenChanged(auth, async (user) => {
        callback(user ? await user.getIdToken() : null, user);
      }),
    )
    .catch((error) =>
      console.error("Could not start the sign-in session", error),
    );
};

export const signOutFirebase = async () => {
  resetVerifier();
  confirmation = null;
  const { auth, signOut } = await loadFirebase();
  await signOut(auth);
};
